import { Guid } from 'guid-typescript'
import { iso, Newtype } from 'newtype-ts'
import { err, ok, Result } from './result'

// Parsing and equality comparisons are funky in `guid-typescript`; plain `string`
// isn't so great either.
// The below also allows to avoid mapping wrapped guid objects when serializing.
// Inspired by Elm's Opaque Types.
// Read more:
// https://github.com/gcanti/newtype-ts

export interface GUID
  extends Newtype<{ readonly GUID: unique symbol }, string> { }

const isoGuid = iso<GUID>()

/**
 * Create a valid guid from nothing.
 *
 * This, and any function that calls it, indirectly or not, is impure.
 * Is it worth upgrading to a seeded-RNG-based guid generator?
 */
export function createGuid(): GUID {
  return isoGuid.wrap(Guid.create().toString())
}

export function parseGuid(s: string): Result<GUID, string> {
  if (Guid.isGuid(s)) {
    return ok(isoGuid.wrap(s))
  }
  return err(`not a valid guid: "${s}"`)
}

export function isGuid(s: string): boolean {
  return parseGuid(s).ok
}

export function equals(g: GUID, s: string): boolean {
  return ((g as any) as string) === s
}

export function guidToString(g: GUID): string {
  return (g as unknown) as string
}
